import * as React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { DocumentRow } from "./DocumentRow";

type DocumentStatus = "draft" | "published" | "archived";

type Document = {
  _id: string;
  title: string;
  status: DocumentStatus;
  updatedAt: number;
  contentLength?: number;
  summary?: string;
  contentPreview?: string;
  tags?: string[];
};

type Subfolder = {
  path: string;
  name: string;
  documentCount: number;
};

type CheckboxCheckedState = boolean | "indeterminate";

type DocumentListProps = {
  documents: Document[];
  subfolders: Subfolder[];
  onDocumentClick: (documentId: string) => void;
  onFolderClick: (folderPath: string) => void;
  selectedDocumentIds?: Set<string>;
  onDocumentSelectionChange?: (documentId: string, selected: boolean) => void;
  onSelectAll?: (selected: boolean) => void;
};

export function DocumentList({
  documents,
  subfolders,
  onDocumentClick,
  onFolderClick,
  selectedDocumentIds,
  onDocumentSelectionChange,
  onSelectAll,
}: DocumentListProps) {
  const allSelected = documents.length > 0 && documents.every((doc) => selectedDocumentIds?.has(doc._id));
  const someSelected = documents.some((doc) => selectedDocumentIds?.has(doc._id));

  const handleSelectAllChange = React.useCallback((checked: CheckboxCheckedState) => {
    if (onSelectAll) {
      onSelectAll(checked === true);
    }
  }, [onSelectAll]);

  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full">
        <thead className="border-b bg-muted/50">
          <tr>
            <th className="py-3 pl-4 pr-3 text-left text-sm font-medium">
              <div className="flex items-center gap-3">
                {onSelectAll && documents.length > 0 && (
                  <Checkbox
                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    onCheckedChange={handleSelectAllChange}
                    aria-label="Select all documents"
                  />
                )}
                <span>Name</span>
              </div>
            </th>
            <th className="px-3 py-3 text-left text-sm font-medium">Status</th>
            <th className="px-3 py-3 text-left text-sm font-medium">Last Modified</th>
            <th className="px-3 py-3 pr-4 text-left text-sm font-medium">Size</th>
          </tr>
        </thead>
        <tbody>
          {subfolders.map((folder) => (
            <DocumentRow
              key={folder.path}
              type="folder"
              title={folder.name}
              documentCount={folder.documentCount}
              onClick={() => onFolderClick(folder.path)}
              onDoubleClick={() => onFolderClick(folder.path)}
            />
          ))}
          {documents.map((doc) => (
            <DocumentRow
              key={doc._id}
              type="document"
              title={doc.title}
              status={doc.status}
              updatedAt={doc.updatedAt}
              contentLength={doc.contentLength}
              summary={doc.summary}
              contentPreview={doc.contentPreview}
              tags={doc.tags}
              onClick={() => onDocumentClick(doc._id)}
              onDoubleClick={() => onDocumentClick(doc._id)}
              documentId={doc._id}
              isSelected={selectedDocumentIds?.has(doc._id)}
              onSelectionChange={
                onDocumentSelectionChange
                  ? (selected) => onDocumentSelectionChange(doc._id, selected)
                  : undefined
              }
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
